// What's still to book, in one place for Bookings on the web, the booking reminders and get_trip's
// count, so the three can't drift apart (features/bookings-and-costs.md). Needing booking is a fact
// about the place (it can't just be walked into); it is to book only once it's also wanted: someone's
// yes, someone going, it's in the plan (planned, a picked option, or put on a day), or a to-do asks for
// it to be booked. A maybe isn't enough. Plain functions, used by the server and the web view.
import type { When } from "./model.ts";

// Someone's say on an item, by person id.
export interface Rating {
  person: string;
  tier: string | null;
  going: string | null;
}

// Anything with an item's shape: the server's stored records (fields spread out) and the web view's
// items alike.
export interface Bookable {
  id: string;
  type: string;
  status: string;
  booking_needed?: boolean | undefined;
  parent?: string | undefined;
  when?: When | undefined;
  interest: readonly Rating[];
}

// Why it's to book, the strongest reason first: the plan's own (a picked option, a day, planned), then a
// to-do for it, then people (going, then yes; each with everyone who said so).
export type Why =
  | { kind: "chosen" }
  | { kind: "day" }
  | { kind: "planned" }
  | { kind: "todo" }
  | { kind: "going" | "yes"; people: string[] };

export interface ToBook<T> {
  item: T;
  why: Why;
  // The open to-do for it, whose owner books it by its due date: the item itself when it's a to-do.
  task: T | null;
}

// Booked or called off, over, or not decided (an option waits for its choice to be made): nothing to
// book.
export const SETTLED = ["booked", "cancelled", "done", "skipped", "missed", "dropped", "option", "not_chosen"];
const settled = new Set(SETTLED);

// Put on a day: a day, days, a part of a given day, or a time.
export function onADay(when: When | undefined): boolean {
  if (!when) return false;
  return when.precision === "part" ? Boolean(when.day) : when.precision !== "none";
}

// What still needs booking and is wanted, in the items' order, each once: a to-do for a thing that
// needs booking goes with that thing. `members`: the people in the trip (by person id); someone who
// left keeps their history, not a say.
export function toBook<T extends Bookable & { owner?: string | undefined }>(
  items: readonly T[],
  members: Iterable<string>,
): ToBook<T>[] {
  const inTrip = new Set(members);
  const byId = new Map(items.map((i) => [i.id, i]));
  const todoFor = new Map<string, T>();
  for (const t of items) {
    if (t.type === "task" && t.status !== "done" && t.parent && !todoFor.has(t.parent)) todoFor.set(t.parent, t);
  }
  const out: ToBook<T>[] = [];
  for (const item of items) {
    if (!item.booking_needed || settled.has(item.status)) continue;
    if (item.type === "task" && item.parent && byId.get(item.parent)?.booking_needed) continue;
    const task = item.type === "task" ? item : (todoFor.get(item.id) ?? null);
    const why = wanted(item, byId.get(item.parent ?? "")?.type === "choice", task !== null, inTrip);
    if (why) out.push({ item, why, task });
  }
  return out;
}

function wanted(item: Bookable, option: boolean, todo: boolean, inTrip: Set<string>): Why | null {
  if (item.status === "planned" && option) return { kind: "chosen" };
  if (onADay(item.when)) return { kind: "day" };
  if (item.status === "planned") return { kind: "planned" };
  if (todo) return { kind: "todo" };
  const said = item.interest.filter((r) => inTrip.has(r.person));
  for (const kind of ["going", "yes"] as const) {
    const people = said.filter((r) => (kind === "going" ? r.going === "yes" : r.tier === kind)).map((r) => r.person);
    if (people.length) return { kind, people };
  }
  return null;
}

// A to-do's title worded as a booking: "Book …", "Reserve …", "Buy tickets …".
export const BOOKING_WORDS = /^\s*(book|reserve|buy tickets?|get tickets?|purchase tickets?)\b/i;

// The thing a to-do books, when it books one: what it's pointed at (`parent`), when the to-do or that thing
// needs booking, as `toBook` pairs them while it's open, or the to-do is worded as booking it ("Book the
// Haneda transfer", pointed at the transfer booked from its confirmation: guide "confirmations"). Once done,
// Bookings shows it in that thing's row (DESIGN.md, Bookings: a to-do that books something shows once, as that
// booking, done too). None for a to-do on nothing, or pointed at something it doesn't book.
export function booksFor<T extends Bookable & { title: string }>(task: T, byId: ReadonlyMap<string, T>): string | null {
  if (task.type !== "task" || !task.parent) return null;
  const thing = byId.get(task.parent);
  if (!thing || thing.type === "task") return null;
  return task.booking_needed || thing.booking_needed || BOOKING_WORDS.test(task.title) ? thing.id : null;
}
